import React, { useState, useMemo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useMutation, useQueryClient, useIsFetching } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { MdDelete, MdPictureAsPdf } from 'react-icons/md';

import API from '@/apiRequest';
import Modal from '@/components/common/Modal';
import ReportOptions from '@/components/ReportOptions/ReportOptions';
import { handleAIVerification, isAIVerificationAvailable } from '@/helpers/aiVerification';
import ModalAIVerification from '@/orderDetails/ModalAIVerification/ModalAIVerification';
import ModalConfirmChanges from '@/orderDetails/ModalConfirmChanges';
import { showToast } from '@/store/ducks/toasts';
import InspectionIcon from '@/svg-icons/inspections';
import { Button } from '@/components/ui/button';

const Inspections = ({
  orderId,
  refreshHandler,
  inspections,
  hasActionAccess,
  updateOrderItem,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isFetchingOrder = useIsFetching({ queryKey: ['order', orderId] }) > 0;

  const [state, setState] = useState({
    showModal: false,
    askForceItem: null,
    openReportList: false,
    anchorEl: null,
    selectedInspection: null,
    currentData: null,
    updatedData: null,
    originalUpdatedData: null,
    showAIModal: false,
    aiValidationResults: null,
  });

  const [aiLoading, setAiLoading] = useState(false);

  const orderTypeConfigurations = useMemo(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem('orderTypeConfigurations')) || {};
    } catch {
      return {};
    }
  }, []);

  const clearConflictState = useCallback(
    () =>
      setState(prev => ({
        ...prev,
        askForceItem: null,
        showModal: false,
        currentData: null,
        updatedData: null,
        originalUpdatedData: null,
      })),
    []
  );

  const makeOfficialMutation = useMutation({
    mutationFn: async ({ id, forceConfirmed, rejectedData, updatedData }) => {
      const force = forceConfirmed === true;
      let data = { inspection_id: id, force };
      if (rejectedData) {
        data.updated_data = updatedData;
      }
      const response = await API.post(`orders/${orderId}/official`, data);
      await queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      return response;
    },
    onSuccess: () => {
      setState(prev => ({
        ...prev,
        askForceItem: null,
        showModal: false,
        currentData: null,
        updatedData: null,
        originalUpdatedData: null,
      }));
    },
    onError: (error, variables) => {
      if (error.response && error.response.status === 409) {
        const payload = error.response.data;
        setState(prev => {
          const nextState = {
            ...prev,
            askForceItem: variables?.id || payload?.id || prev.askForceItem,
          };
          if (payload?.data_conflict) {
            nextState.currentData = payload.current_data;
            nextState.updatedData = payload.updated_data;
            nextState.originalUpdatedData = payload.updated_data
              ? JSON.parse(JSON.stringify(payload.updated_data))
              : null;
            if (!payload.official_inspection_conflict) {
              nextState.showModal = true;
            }
          }
          return nextState;
        });
        return;
      }

      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
    },
  });

  const unassignMutation = useMutation({
    mutationFn: id => API.delete(`inspections/${id}`),
    onSuccess: res => {
      updateOrderItem({ ...res.data });
      refreshHandler();
    },
    onError: error => {
      console.error(error);
    },
  });

  const handleReportClick = (event, inspection) => {
    const { currentTarget } = event;
    setState(prev => ({
      ...prev,
      anchorEl: currentTarget,
      openReportList: !prev.openReportList,
      selectedInspection: inspection,
    }));
  };

  const handleAIVerificationInternal = inspectionId => {
    const onSuccess = validationResults => {
      setState(prev => ({
        ...prev,
        aiValidationResults: validationResults,
        showAIModal: true,
      }));
      setAiLoading(false);
    };

    const onError = error => {
      console.error(error);
      setAiLoading(false);
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
    };

    setAiLoading(true);
    handleAIVerification(inspectionId, onSuccess, onError, setAiLoading);
  };

  const currentInspection = state.selectedInspection || {};
  const reportsOptions = useMemo(() => {
    if (
      currentInspection &&
      currentInspection.type &&
      orderTypeConfigurations[currentInspection.type] &&
      orderTypeConfigurations[currentInspection.type][currentInspection.progress_status]
    ) {
      return orderTypeConfigurations[currentInspection.type][currentInspection.progress_status].reportsOptions || [];
    }
    return [];
  }, [currentInspection, orderTypeConfigurations]);

  if (!hasActionAccess || inspections.length === 0) return null;

  const filteredInspections = inspections.filter(inspection => inspection.official !== true);
  if (filteredInspections.length === 0) return null;

  const sectionTextClass = 'font-bold text-sm my-2 text-[#222]';
  const legacyContainedButtonClass =
    'h-[36px]! min-h-[36px]! min-w-[64px]! rounded-[4px]! border-0! bg-[#e0e0e0]! px-4! py-2! text-[14px]! font-medium! uppercase! text-[rgba(0,0,0,0.87)]! shadow-[0_1px_5px_0_rgba(0,0,0,0.2),0_2px_2px_0_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)]! hover:bg-[#d5d5d5]!';
  const legacyContainedButtonStyle = {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: '0 1px 5px 0 rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.12)',
  };

  return (
    <>
      <div className="col-span-12" style={{ fontFamily: 'Open Sans, sans-serif' }}>
        <div className={`flex items-center ${sectionTextClass}`}>
          <InspectionIcon
            viewBox="0 0 34.016 34.016"
            className="text-gray-700 mr-2 w-6 h-6 shrink-0 align-middle"
          />
          <FormattedMessage id="inspections" />
        </div>

        <ul
          className="mb-3 pt-0! pl-8! list-none! m-0!"
          data-cy="order-details-inspections-list"
        >
          {filteredInspections.map((inspection, i) => {
            const inspector = inspection.inspector || intl.formatMessage({ id: 'UNIDENTIFIED_INSPECTOR_MESSAGE' });
            const date = new Date(inspection.last_update);
            const datetime = date ? `${intl.formatDate(date)} ${intl.formatTime(date)}` : null;

            let message = '';
            switch (inspection.progress_status) {
              case 'FINALIZED':
                message = `${intl.formatMessage({ id: 'INSPECTION_CARRIED_OUT_IN' })} ${datetime}`;
                break;
              case 'PENDING':
                message = `${intl.formatMessage({ id: 'INSPECTION_ASSIGNED_OUT_IN' })} ${datetime}`;
                break;
              case 'IN_PROGRESS':
                message = intl.formatMessage({ id: 'INSPECTION_IN_PROGRESS' });
                break;
              default:
                break;
            }

            const isAskingForce = inspection.id === state.askForceItem && !state.showModal;
            const loading = makeOfficialMutation.isPending || unassignMutation.isPending || isFetchingOrder;
            const canManageInspection = inspection.progress_status === 'FINALIZED';

            return (
              <React.Fragment key={inspection.id}>
                <li className="flex flex-col items-start pl-0! pr-6! text-[12px] py-3 leading-[18px]">
                  <div className="pr-4">
                    {isAskingForce ? (
                      <span className="block text-[14px] font-bold leading-[21px] text-[#858585]">
                        <FormattedMessage id="ORDER_ALREADY_HAVE_AN_INSPECTION_MESSAGE" />
                      </span>
                    ) : (
                      <>
                        <div className="text-[14px] font-bold leading-[21px] text-[#858585]">{message}</div>
                        <p className="m-0 text-[12px] leading-[17.57px] text-[#858585]">
                          {intl.formatMessage({ id: 'EXECUTED_BY' })} {inspector}
                        </p>
                      </>
                    )}
                  </div>

                  <div className="flex flex-nowrap mt-[10px] -mx-[5px] gap-0 items-center">
                    {isAskingForce ? (
                      <>
                        <Button
                          variant="ghost"
                          className={`${legacyContainedButtonClass} !mx-[5px]`}
                          style={legacyContainedButtonStyle}
                          onClick={clearConflictState}
                        >
                          <FormattedMessage id="CANCEL" />
                        </Button>
                        <Button
                          variant="ghost"
                          className={`${legacyContainedButtonClass} !mx-[5px]`}
                          style={legacyContainedButtonStyle}
                          disabled={loading}
                          onClick={() =>
                            !state.currentData && !state.updatedData
                              ? makeOfficialMutation.mutate({ id: inspection.id, forceConfirmed: true })
                              : setState(prev => ({ ...prev, showModal: true }))
                          }
                        >
                          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FormattedMessage id="REPLACE" />}
                        </Button>
                      </>
                    ) : (
                      <>
                        {canManageInspection && (
                          <Button
                            variant="ghost"
                            className={`${legacyContainedButtonClass} !mx-[5px]`}
                            style={legacyContainedButtonStyle}
                            disabled={loading}
                            onClick={() => makeOfficialMutation.mutate({ id: inspection.id })}
                          >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FormattedMessage id="COPY" />}
                          </Button>
                        )}

                        {canManageInspection && isAIVerificationAvailable(inspection.type) && (
                          <Button
                            variant="ghost"
                            className={`${legacyContainedButtonClass} !mx-[5px]`}
                            style={legacyContainedButtonStyle}
                            disabled={loading || aiLoading}
                            onClick={() => handleAIVerificationInternal(inspection.id)}
                          >
                            {aiLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FormattedMessage id="REVIEW" />
                            )}
                          </Button>
                        )}

                        {canManageInspection && (
                          <Button
                            variant="ghost"
                            className={`${legacyContainedButtonClass} !mx-[5px]`}
                            style={legacyContainedButtonStyle}
                            disabled={loading}
                            onClick={e => handleReportClick(e, inspection)}
                          >
                            <MdPictureAsPdf className="h-6 w-12 min-w-12 text-[rgba(0,0,0,0.87)]" />
                          </Button>
                        )}
                      </>
                    )}

                    <Button
                      variant="ghost"
                      className={`${legacyContainedButtonClass} !mx-[5px]`}
                      style={legacyContainedButtonStyle}
                      disabled={loading}
                      onClick={() => unassignMutation.mutate(inspection.id)}
                    >
                      <MdDelete className="h-6 w-12 min-w-12 text-[rgba(0,0,0,0.87)]" />
                    </Button>
                  </div>
                </li>
                {filteredInspections.length !== i + 1 && <hr className="w-full border-t border-[#E9E9E9]" />}
              </React.Fragment>
            );
          })}
        </ul>
      </div>

      <ReportOptions
        show={state.openReportList}
        closeHandler={() => setState(prev => ({ ...prev, openReportList: false }))}
        anchorEl={state.anchorEl}
        options={reportsOptions}
        entityId={currentInspection.id}
      />

      {state.showModal && (
        <Modal onClose={clearConflictState}>
          <ModalConfirmChanges
            onClose={clearConflictState}
            currentData={state.currentData}
            updatedData={state.updatedData}
            originalUpdatedData={state.originalUpdatedData}
            changeUpdatedData={(key, value) =>
              setState(prev => {
                if (!prev.updatedData) return prev;
                return {
                  ...prev,
                  updatedData: {
                    ...prev.updatedData,
                    details: {
                      ...(prev.updatedData.details || {}),
                      [key]: value,
                    },
                  },
                };
              })
            }
            makeOfficialHandler={(id, force, rejected) =>
              makeOfficialMutation.mutate({
                id,
                forceConfirmed: force,
                rejectedData: rejected,
                updatedData: state.updatedData,
              })
            }
            inspectionId={state.askForceItem}
            loading={makeOfficialMutation.isPending}
          />
        </Modal>
      )}

      {state.showAIModal && (
        <Modal
          onClose={() => setState(prev => ({ ...prev, showAIModal: false }))}
          extraStyles={{ maxWidth: '92vw', width: '92vw', padding: '0' }}
        >
          <ModalAIVerification
            onClose={() => setState(prev => ({ ...prev, showAIModal: false }))}
            validationResults={state.aiValidationResults}
            loading={aiLoading}
          />
        </Modal>
      )}
    </>
  );
};

Inspections.propTypes = {
  orderId: PropTypes.string.isRequired,
  refreshHandler: PropTypes.func.isRequired,
  inspections: PropTypes.array.isRequired,
  hasActionAccess: PropTypes.bool.isRequired,
  updateOrderItem: PropTypes.func.isRequired,
};

export default Inspections;
